import { motion } from 'framer-motion';
import * as Lucide from 'lucide-react';
import PageHero from '../components/aravi/PageHero';
import Enquiry from '../components/aravi/Enquiry';
import { SUSTAINABILITY_PAGE } from '../constants/testIds';
import { SUSTAINABILITY } from '../data/content';
import { CERTIFICATIONS, AMENITY_GROUPS } from '../data/pages';

const IMPACT = [
  { value: '25', label: 'Acres of Land' },
  { value: '110', label: 'Villas' },
  { value: '75%', label: 'Natural Oxygen' },
  { value: '40+', label: 'Curated Amenities' },
];

export default function SustainabilityPage() {
  return (
    <div data-testid={SUSTAINABILITY_PAGE.root}>
      <PageHero
        eyebrow="Sustainability & Amenities"
        title={<>Lived-in by<br/>the land itself.</>}
        intro="A Miyawaki forest belt, a working natural farm, solar on the commons, rainwater across every street, and forty-plus shared amenities — composed so the community feels less like real estate and more like a village square."
        image="https://images.unsplash.com/photo-1778037236171-47f59f5e16bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxuYXR1cmUlMjBsaWZlc3R5bGUlMjByZWFkaW5nJTIwZm9yZXN0fGVufDB8fHx8MTc4MjczMTIzNnww&ixlib=rb-4.1.0&q=85"
      />

      {/* Impact stats */}
      <section className="bg-[#18342A] text-[#F5F1E8] border-y border-[#C9A458]/20">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto py-12 grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6">
          {IMPACT.map((s) => (
            <div key={s.label}>
              <div className="font-serif text-4xl md:text-5xl text-[#C9A458] leading-none">{s.value}</div>
              <div className="mt-3 text-[10px] tracking-[0.4em] uppercase text-[#9BA58D]">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Four covenants */}
      <section className="bg-[#F5F1E8] py-24 lg:py-32">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto">
          <div className="mb-14">
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#A66B45]">
              The Four Covenants
            </span>
            <h2 className="font-serif text-[#18342A] text-4xl md:text-5xl lg:text-6xl mt-6 leading-tight font-light max-w-3xl">
              Built lightly.<br/>Lived in deeply.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#18342A]/10">
            {SUSTAINABILITY.map((s, i) => {
              const Icon = Lucide[s.icon] ?? Lucide.Leaf;
              return (
                <motion.div
                  key={s.title}
                  data-testid={SUSTAINABILITY_PAGE.promise(i)}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className="bg-[#F5F1E8] p-8 lg:p-10 hover:bg-white transition-colors"
                >
                  <div className="flex items-start justify-between mb-10">
                    <span className="text-[#6D6A61] text-[10px] tracking-[0.4em] uppercase">
                      0{i + 1}
                    </span>
                    <Icon size={36} strokeWidth={1.2} className="text-[#A66B45]" />
                  </div>
                  <h3 className="font-serif text-[#18342A] text-2xl lg:text-3xl leading-snug mb-4 font-light">
                    {s.title}
                  </h3>
                  <p className="text-[#6D6A61] text-sm leading-relaxed font-light">
                    {s.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Amenity groups */}
      <section className="bg-[#9BA58D]/15 py-24 lg:py-32">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <span className="text-[11px] tracking-[0.4em] uppercase text-[#A66B45]">
                The Forty
              </span>
              <h2 className="font-serif text-[#18342A] text-4xl md:text-5xl lg:text-6xl mt-6 leading-tight font-light max-w-2xl">
                Every amenity<br/>that makes this home.
              </h2>
            </div>
            <p className="text-[#6D6A61] text-sm font-light max-w-md">
              Grouped by how you&apos;ll actually use them — the land that breathes, the body that moves, the community that gathers, the home that rests.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
            {AMENITY_GROUPS.map((g, i) => (
              <motion.div
                key={g.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex items-baseline gap-3 mb-6">
                  <span className="text-[#C9A458] font-serif text-2xl">0{i + 1}</span>
                  <h3 className="font-serif text-[#18342A] text-2xl md:text-3xl leading-tight font-light">
                    {g.title}
                  </h3>
                </div>
                <ul className="space-y-3 border-l border-[#C9A458]/30 pl-6">
                  {g.items.map((it) => (
                    <li key={it} className="flex items-center gap-3 text-[#18342A]/85 text-sm font-light">
                      <span className="w-1 h-1 rounded-full bg-[#A66B45]" />
                      {it}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="bg-[#F5F1E8] py-20 lg:py-24">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto">
          <div className="hairline w-full mb-12" />
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12">
            <h2 className="font-serif text-[#18342A] text-3xl md:text-4xl lg:text-5xl leading-tight font-light max-w-xl">
              Documents that<br/>say the same thing<br/>to everyone.
            </h2>
            <p className="text-[#6D6A61] text-sm font-light max-w-md">
              Approval letters, plot titles, the published rate of ₹10,999 per sq yard, and our monthly work-progress update — all available on request.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#18342A]/10">
            {CERTIFICATIONS.map((c) => (
              <div key={c.name} className="bg-[#F5F1E8] p-8 text-center flex flex-col items-center justify-start gap-5 min-h-[220px]">
                {c.logo ? (
                  <div className="w-24 h-24 grid place-items-center">
                    <img
                      src={c.logo}
                      alt={c.name}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : c.showRupee ? (
                  <div className="w-24 h-24 rounded-full border-2 border-[#C9A458] grid place-items-center">
                    <img
                      src="/certifications/rupee.png"
                      alt="Indian Rupee"
                      className="w-12 h-12 object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-full border-2 border-[#C9A458] grid place-items-center font-serif text-[#18342A]">
                    <span className="text-[11px] tracking-[0.2em] font-medium">{c.mark}</span>
                  </div>
                )}
                <div className="text-[11px] tracking-[0.28em] uppercase text-[#18342A] leading-relaxed max-w-[18ch]">
                  {c.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Enquiry />
    </div>
  );
}
